import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  Typography,
} from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { NavLink, useLocation } from 'react-router-dom';
import { expeditionBillingNavigationMenu } from '../config/navigation';
import { billingLogoUrl } from '../config/branding';
import './Sidebar.css';

const APP_VERSION = '1.0.0';

function Sidebar({ open, mobile, collapsed, onClose, onToggleCollapse }) {
  const location = useLocation();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    if (mobile && open && previousPathname.current !== location.pathname) {
      onClose?.();
    }

    previousPathname.current = location.pathname;
  }, [location.pathname, mobile, onClose, open]);

  if (mobile) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 'min(88vw, 256px)',
            background:
              'linear-gradient(180deg, rgba(255, 121, 0, 0.06), rgba(255, 121, 0, 0) 38%), #071017',
            color: '#ffffff',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            overflow: 'hidden',
          },
        }}
        slotProps={{ paper: { className: 'suite-sidebar-drawer' } }}
      >
        <Box className="suite-sidebar-mobile">
          <SidebarContent
            collapsed={false}
            mobile
            onClose={onClose}
          />
        </Box>
      </Drawer>
    );
  }

  return (
    <Box
      component="aside"
      className={`suite-sidebar ${collapsed ? 'suite-sidebar-collapsed' : ''
        }`}
    >
      <SidebarContent
        collapsed={collapsed}
        mobile={false}
        onClose={onClose}
      />
    </Box>
  );
}

function SidebarContent({ collapsed, mobile, onClose }) {
  const location = useLocation();
  const groupedPaths = useMemo(
    () => expeditionBillingNavigationMenu
      .filter((item) => item.items)
      .reduce((result, item) => ({
        ...result,
        [item.label]: item.items.map((child) => child.path),
      }), {}),
    []
  );
  const [expandedGroups, setExpandedGroups] = useState(() =>
    Object.fromEntries(
      Object.entries(groupedPaths).map(([label, paths]) => [
        label,
        paths.includes(location.pathname),
      ])
    )
  );

  useEffect(() => {
    Object.entries(groupedPaths).forEach(([label, paths]) => {
      if (paths.includes(location.pathname)) {
        setExpandedGroups((current) =>
          current[label] ? current : { ...current, [label]: true }
        );
      }
    });
  }, [groupedPaths, location.pathname]);

  const toggleGroup = (label) => {
    setExpandedGroups((current) => ({ ...current, [label]: !current[label] }));
  };

  return (
    <Box className="suite-sidebar-content">
      <Box className="suite-brand">
        <img
          src={billingLogoUrl}
          alt="Facturación de Expediciones"
          className="suite-brand-logo"
        />
      </Box>

      <Box className="suite-sidebar-section">
        <Typography className="suite-sidebar-label">Facturación</Typography>
        {expeditionBillingNavigationMenu.map((item) => item.items ? (
          <NavigationGroup
            key={item.label}
            group={item}
            expanded={Boolean(expandedGroups[item.label])}
            collapsed={collapsed}
            mobile={mobile}
            onClose={onClose}
            onToggle={() => toggleGroup(item.label)}
          />
        ) : (
          <NavButton
            key={item.path}
            item={item}
            collapsed={collapsed}
            mobile={mobile}
            onClose={onClose}
          />
        ))}
      </Box>

      <Box className="suite-sidebar-footer">
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)', mb: 1.5 }} />
        <Typography className="suite-footer-copy">
          &copy; 2026 <strong>Clasol</strong>
        </Typography>
        <Typography className="suite-footer-version">
          Version {APP_VERSION}
        </Typography>
      </Box>
    </Box>
  );
}

function NavigationGroup({ collapsed, expanded, group, mobile, onClose, onToggle }) {
  return (
    <Box className="suite-nav-group">
      <Button
        className="suite-nav-group-button"
        aria-expanded={expanded}
        aria-label={group.label}
        title={collapsed ? group.label : undefined}
        endIcon={expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
        onClick={onToggle}
      >
        <span className="suite-nav-label">{group.label}</span>
      </Button>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box className="suite-nav-group-items">
          {group.items.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              collapsed={collapsed}
              mobile={mobile}
              nested
              onClose={onClose}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function NavButton({ item, collapsed, mobile, nested = false, onClose }) {
  const Icon = item.icon;

  return (
    <Button
      component={NavLink}
      to={item.path}
      end={item.path === '/'}
      startIcon={<Icon fontSize="small" />}
      className={`suite-nav-button suite-nav-button-muted${nested ? ' suite-nav-button-nested' : ''}`}
      aria-label={item.label}
      title={collapsed ? item.label : undefined}
      onClick={() => {
        if (mobile) {
          onClose?.();
        }
      }}
    >
      <span className="suite-nav-label">{item.label}</span>
    </Button>
  );
}

export default Sidebar;
